// Member presence on the viewer's side: one read per tab, however many
// surfaces show an "Active now" dot.
//
// Each surface subscribes the member ids it is currently rendering (inbox rows,
// dock rows, an open thread's header, chat heads). The provider keeps a
// ref-counted union of those ids, and every subscriber reads the same
// `membersOnline` query keyed on that union — so the tab polls once per
// presence interval, not once per surface, and only asks about members that
// are actually on screen.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserId } from '@leanin/shared/ids';

import { identityKeys } from './queries/identity';

interface PresenceSubscriptions {
  subscribe(ids: readonly UserId[]): () => void;
  onChange(listener: () => void): () => void;
  ids(): readonly UserId[];
}

function createPresenceSubscriptions(): PresenceSubscriptions {
  const counts = new Map<UserId, number>();
  const listeners = new Set<() => void>();
  let snapshot: readonly UserId[] = [];

  const publish = () => {
    snapshot = [...counts.keys()].sort();
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(ids) {
      const unique = [...new Set(ids)];
      let grew = false;
      for (const id of unique) {
        const n = counts.get(id) ?? 0;
        if (n === 0) grew = true;
        counts.set(id, n + 1);
      }
      if (grew) publish();
      return () => {
        let shrank = false;
        for (const id of unique) {
          const n = counts.get(id) ?? 0;
          if (n <= 1) {
            counts.delete(id);
            shrank = true;
          } else {
            counts.set(id, n - 1);
          }
        }
        if (shrank) publish();
      };
    },
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ids: () => snapshot,
  };
}

const PresenceContext = createContext<PresenceSubscriptions | null>(null);

export function PresenceSubscriptionsProvider({ children }: { children: ReactNode }) {
  const [subscriptions] = useState(createPresenceSubscriptions);
  return <PresenceContext.Provider value={subscriptions}>{children}</PresenceContext.Provider>;
}

const NO_ONE_ONLINE: ReadonlySet<UserId> = new Set();

// The subset of `ids` currently online. Members not yet covered by a completed
// read (just subscribed, first read in flight) read as offline.
export function useOnlineMembers(ids: readonly UserId[]): ReadonlySet<UserId> {
  const subscriptions = useContext(PresenceContext);
  if (subscriptions === null) {
    throw new Error('useOnlineMembers must be used within <PresenceSubscriptionsProvider>.');
  }
  const idsKey = [...ids].sort().join(',');
  useEffect(
    () => subscriptions.subscribe(idsKey === '' ? [] : (idsKey.split(',') as UserId[])),
    [subscriptions, idsKey],
  );
  const subscribed = useSyncExternalStore(subscriptions.onChange, subscriptions.ids);
  return useQuery(identityKeys.membersOnline(subscribed)).data ?? NO_ONE_ONLINE;
}
